import {hasHiddenUnicodeCharacters} from '@github-ui/hidden-unicode-banner/utils'
import type {SourceRange} from '@github-ui/taillights-worker/document'
import {useMemo} from 'react'

import {lastSelectedLine} from './source-navigation'
import {
  type TaillightsGutterAnnotation,
  TaillightsGutterRow,
  type TaillightsGutterRowProps,
} from './TaillightsGutterRow'
import type {GutterActions} from './use-gutter-selection'

const emptyActions: TaillightsGutterRowProps['actions'] = []

export type GutterContentProps = {
  visible: readonly number[]
  foldStarts: ReadonlySet<number>
  foldingReady?: boolean
  collapsed: ReadonlySet<number>
  anchorRange: SourceRange | null
  onToggle: (startLine: number) => void
  actions: GutterActions
  lines: readonly string[]
  codeownersErrors?: Readonly<Record<number, string>>
  showLineActions: boolean
  onLineMount?: (line: number, element: HTMLAnchorElement) => void | (() => void)
}

type PreparedGutterRow = Pick<
  TaillightsGutterRowProps,
  'lineNumber' | 'top' | 'edge' | 'lane' | 'fold' | 'annotations' | 'ref'
> & {sourceLine: number}

export function TaillightsGutterRows({
  visible,
  rows,
  lineHeight,
  tabStopLine,
  onFocusChange,
  foldStarts,
  foldingReady = true,
  collapsed,
  anchorRange,
  onToggle,
  actions,
  lines,
  codeownersErrors,
  showLineActions,
  onLineMount,
}: GutterContentProps & {
  rows: ReadonlyArray<{index: number; top: number}>
  lineHeight: number
  tabStopLine: number | null
  onFocusChange: (line: number | null) => void
}) {
  const preparedRows = useMemo(
    () =>
      rows.map((item): PreparedGutterRow => {
        const sourceLine = visible[item.index]
        if (sourceLine === undefined) throw new Error(`Gutter row ${item.index} is outside the source projection.`)
        const lineNumber = sourceLine + 1
        const annotations: TaillightsGutterAnnotation[] = []
        if (hasHiddenUnicodeCharacters(lines[sourceLine] ?? '')) {
          annotations.push({
            kind: 'unicode',
            message:
              'Bidirectional or hidden Unicode can change how code is interpreted. Review in an editor that reveals hidden characters.',
          })
        }
        const error = codeownersErrors?.[lineNumber]
        if (error) annotations.push({kind: 'codeowners', message: error})
        return {
          sourceLine,
          lineNumber,
          top: item.top,
          edge: item.index === 0 ? 'start' : item.index === visible.length - 1 ? 'end' : undefined,
          lane: item.index % 2 === 0 ? 0 : 1,
          fold: foldStarts.has(lineNumber)
            ? {startLine: lineNumber, collapsed: collapsed.has(lineNumber), disabled: !foldingReady}
            : undefined,
          annotations: annotations.length ? annotations : undefined,
          ref: onLineMount ? node => (node ? onLineMount(lineNumber, node) : undefined) : undefined,
        }
      }),
    [rows, visible, lines, codeownersErrors, foldStarts, foldingReady, collapsed, onLineMount],
  )

  return (
    <>
      {preparedRows.map(({sourceLine, lineNumber, top, edge, lane, fold, annotations, ref}, index) => {
        return (
          <TaillightsGutterRow
            key={sourceLine}
            ref={ref}
            lineNumber={lineNumber}
            top={top}
            gridRow={index + 1}
            edge={edge}
            lane={lane}
            fold={fold}
            annotations={annotations}
            height={lineHeight}
            selected={
              !!anchorRange && sourceLine >= anchorRange.start.line && sourceLine <= lastSelectedLine(anchorRange)
            }
            rangeStart={sourceLine === anchorRange?.start.line}
            tabStop={lineNumber === tabStopLine}
            onToggle={onToggle}
            onLine={actions.onLine}
            onSnapshot={actions.captureSnapshot}
            onFocusChange={onFocusChange}
            menuOpen={actions.owner?.lineNumber === lineNumber && actions.owner.kind === 'menu'}
            showLineActions={showLineActions}
            annotationOpen={actions.owner?.lineNumber === lineNumber && actions.owner.kind === 'annotation'}
            onMenuOpenChange={actions.onMenuOpenChange}
            onAnnotationOpenChange={actions.onAnnotationOpenChange}
            actions={
              actions.actionLine === undefined || actions.actionLine === lineNumber ? actions.actions : emptyActions
            }
            menuLabel={actions.menuLabel}
          />
        )
      })}
    </>
  )
}
